import {
  cheatCommand,
  cheatGroup,
  defineCheatPanel,
  type CheatParamValue,
  type CheatPanelSpec,
  type CheatParamValues,
} from "@yjsgames/phaser-cheat/registry";
import { parseSuffixNumber } from "@yjsgames/bignumber";
import { JsonTutorialScenarioSource } from "../tutorial/JsonTutorialScenarioSource.js";
import { WORLD_DEFS } from "../data/worldData.js";
import { allDialogueIds } from "../data/dialogueData.js";
import { buildBattleDialoguePresentation } from "../ui/recordTabData.js";
import type { GameBoot } from "./BootTypes.js";
import { previewHouseAd } from "./cheatHouseAdPreview.js";

const TUTORIAL_SCENARIO_IDS = new JsonTutorialScenarioSource().getScenarioIds();
const DIALOGUE_IDS = allDialogueIds();
const BUTTON_W = 160;

function finiteNumber(n: number): number | null {
  return Number.isFinite(n) ? n : null;
}

function suffix(raw: string): CheatParamValue | null {
  return finiteNumber(parseSuffixNumber(raw));
}

function numeric(raw: string): CheatParamValue | null {
  return finiteNumber(Number(raw.trim()));
}

function valueNumber(values: CheatParamValues, id: string): number {
  return values[id] as number;
}

function valueBool(values: CheatParamValues, id: string): boolean {
  return values[id] as boolean;
}

function valueString(values: CheatParamValues, id: string): string {
  return values[id] as string;
}

export function buildModuleBrickCheatPanel(boot: GameBoot): CheatPanelSpec {
  const cheats = boot.cheats();
  const scenarioIds = TUTORIAL_SCENARIO_IDS.length > 0 ? TUTORIAL_SCENARIO_IDS : ["main"];
  const dialogueOptions = DIALOGUE_IDS.length > 0 ? DIALOGUE_IDS : ["(none)"];
  const worldOptions = WORLD_DEFS.map((world) => String(world.worldNo));
  return defineCheatPanel({
    title: "치트",
    groups: [
      cheatGroup("currency", "재화", { defaultExpanded: true }),
      cheatGroup("progress", "진행", { defaultExpanded: true }),
      cheatGroup("grid", "그리드", { defaultExpanded: true }),
      cheatGroup("ads", "광고/IAP", { defaultExpanded: false }),
      cheatGroup("tutorial", "튜토리얼", { defaultExpanded: false }),
      cheatGroup("dialogue", "대화", { defaultExpanded: false }),
      cheatGroup("debug", "디버그", { defaultExpanded: false }),
    ],
    commands: [
      cheatCommand({
        id: "addGold",
        label: "골드 추가",
        group: "currency",
        buttonWidth: BUTTON_W,
        params: [{ id: "amount", label: "수량", type: "text", defaultValue: "1K", parse: suffix }],
        run: (values) => cheats.addGold(valueNumber(values, "amount")),
      }),
      cheatCommand({
        id: "setGoldGainMultiplier",
        label: "골드 배율",
        group: "currency",
        buttonWidth: BUTTON_W,
        params: [
          {
            id: "multiplier",
            label: "배율",
            type: "number",
            defaultValue: cheats.goldGainMultiplier,
            parse: numeric,
          },
        ],
        run: (values) => cheats.setGoldGainMultiplier(valueNumber(values, "multiplier")),
      }),
      cheatCommand({
        id: "addDiamond",
        label: "다이아 추가",
        group: "currency",
        buttonWidth: BUTTON_W,
        params: [{ id: "amount", label: "수량", type: "text", defaultValue: "200", parse: suffix }],
        run: (values) => cheats.addDiamond(valueNumber(values, "amount")),
      }),
      cheatCommand({
        id: "setTickets",
        label: "던전권 설정",
        group: "currency",
        buttonWidth: BUTTON_W,
        params: [{ id: "tickets", label: "개수", type: "number", defaultValue: 3, parse: numeric }],
        run: (values) => cheats.setTickets(valueNumber(values, "tickets")),
      }),
      cheatCommand({
        id: "setChapter",
        label: "챕터 이동",
        group: "progress",
        buttonWidth: BUTTON_W,
        params: [{ id: "chapter", label: "챕터", type: "number", defaultValue: 30, parse: numeric }],
        run: (values) => cheats.setChapter(valueNumber(values, "chapter")),
      }),
      cheatCommand({
        id: "setWorld",
        label: "월드 이동",
        group: "progress",
        buttonWidth: BUTTON_W,
        params: [{ id: "world", label: "월드", type: "select", options: worldOptions, defaultValue: worldOptions[0] ?? "1" }],
        run: (values) => cheats.setWorld(Number(valueString(values, "world"))),
      }),
      cheatCommand({
        id: "unlockAllTabs",
        label: "모든 탭 잠금 해제",
        group: "progress",
        buttonWidth: BUTTON_W,
        run: () => cheats.unlockAllTabs(),
      }),
      cheatCommand({
        id: "unlockAllSpaceships",
        label: "모든 우주선 획득",
        group: "progress",
        buttonWidth: BUTTON_W,
        run: () => cheats.unlockAllSpaceships(),
      }),
      cheatCommand({
        id: "grantAllRelics",
        label: "모든 유물 획득",
        group: "progress",
        buttonWidth: BUTTON_W,
        run: () => cheats.grantAllRelics(),
      }),
      cheatCommand({
        id: "fillGrid",
        label: "그리드 꽉 채우기",
        group: "grid",
        buttonWidth: BUTTON_W,
        run: () => cheats.fillGridRandomly(),
      }),
      cheatCommand({
        id: "placeRandomSynergy",
        label: "랜덤 시너지 배치",
        group: "grid",
        buttonWidth: BUTTON_W,
        run: () => cheats.placeRandomSynergyPipes(),
      }),
      cheatCommand({
        id: "forceMystery",
        label: "미스터리 강제",
        group: "grid",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: cheats.forceMysteryEnabled }],
        run: (values) => {
          if (valueBool(values, "enabled") !== cheats.forceMysteryEnabled) cheats.toggleForceMystery();
        },
      }),
      cheatCommand({
        id: "forceSpecial",
        label: "특수 벽돌 강제",
        group: "grid",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: cheats.forceSpecialEnabled }],
        run: (values) => {
          if (valueBool(values, "enabled") !== cheats.forceSpecialEnabled) cheats.toggleForceSpecial();
        },
      }),
      cheatCommand({
        id: "forceDailyReset",
        label: "일일 리셋 강제",
        group: "debug",
        buttonWidth: BUTTON_W,
        run: () => cheats.forceDailyReset(),
      }),
      cheatCommand({
        id: "perfOverlay",
        label: "성능 오버레이",
        group: "debug",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: boot.perfOverlayEnabled() }],
        run: (values) => {
          if (valueBool(values, "enabled") !== boot.perfOverlayEnabled()) boot.togglePerfOverlay();
        },
      }),
      cheatCommand({
        id: "appTimerOverlay",
        label: "앱 타이머 표시",
        group: "debug",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: cheats.appTimerOverlayEnabled }],
        run: (values) => {
          if (valueBool(values, "enabled") !== cheats.appTimerOverlayEnabled) cheats.toggleAppTimerOverlay();
        },
      }),
      cheatCommand({
        id: "forceCrash",
        label: "강제 크래시",
        group: "debug",
        buttonWidth: BUTTON_W,
        run: () => cheats.forceCrash(),
      }),
      cheatCommand({
        id: "triggerChapterDefeat",
        label: "챕터 패배 연출",
        group: "debug",
        buttonWidth: BUTTON_W,
        run: () => cheats.triggerChapterDefeat(),
      }),
      cheatCommand({
        id: "previewRebirthTransition",
        label: "환생 연출 확인",
        group: "debug",
        buttonWidth: BUTTON_W,
        run: () => cheats.previewRebirthTransition(),
      }),
      cheatCommand({
        id: "resetAdCooldown",
        label: "광고 쿨타임 리셋",
        group: "ads",
        buttonWidth: BUTTON_W,
        run: () => cheats.resetAdGates(),
      }),
      cheatCommand({
        id: "forceHouseAd",
        label: "인하우스 광고 강제",
        group: "ads",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: boot.cheatForceHouseAd() }],
        run: (values) => {
          if (valueBool(values, "enabled") !== boot.cheatForceHouseAd()) cheats.toggleForceHouseAd();
        },
      }),
      cheatCommand({
        id: "previewRewardAd",
        label: "보상형 광고 미리보기",
        group: "ads",
        buttonWidth: BUTTON_W,
        run: () => previewHouseAd(boot, "reward"),
      }),
      cheatCommand({
        id: "previewInterstitialAd",
        label: "전면 광고 미리보기",
        group: "ads",
        buttonWidth: BUTTON_W,
        run: () => previewHouseAd(boot, "interstitial"),
      }),
      cheatCommand({
        id: "bannerPreview",
        label: "배너 미리보기",
        group: "ads",
        buttonWidth: BUTTON_W,
        params: [{ id: "enabled", label: "활성", type: "boolean", defaultValue: cheats.bannerPreviewEnabled }],
        run: (values) => {
          if (valueBool(values, "enabled") !== cheats.bannerPreviewEnabled) cheats.toggleBannerPreview();
        },
      }),
      cheatCommand({
        id: "buyRemoveAds",
        label: "광고제거 구매",
        group: "ads",
        buttonWidth: BUTTON_W,
        run: () => boot.commands.iap.buyRemoveAds(),
      }),
      cheatCommand({
        id: "clearAdFree",
        label: "광고제거 해제",
        group: "ads",
        buttonWidth: BUTTON_W,
        run: () => cheats.clearAdFree(),
      }),
      cheatCommand({
        id: "startTutorial",
        label: "튜토리얼 재생",
        group: "tutorial",
        buttonWidth: BUTTON_W,
        params: [{ id: "scenario", label: "시나리오", type: "select", options: scenarioIds, defaultValue: scenarioIds[0] }],
        run: (values) => boot.tutorial.start(valueString(values, "scenario")),
      }),
      cheatCommand({
        id: "resetTutorial",
        label: "튜토리얼 리셋",
        group: "tutorial",
        buttonWidth: BUTTON_W,
        params: [{ id: "scenario", label: "시나리오", type: "select", options: scenarioIds, defaultValue: scenarioIds[0] }],
        run: (values) => boot.tutorial.reset(valueString(values, "scenario")),
      }),
      cheatCommand({
        id: "playDialogue",
        label: "대화 재생",
        group: "dialogue",
        buttonWidth: BUTTON_W,
        params: [{ id: "dialogueId", label: "대화 ID", type: "select", options: dialogueOptions, defaultValue: dialogueOptions[0] }],
        run: (values) => {
          const id = valueString(values, "dialogueId");
          const data = buildBattleDialoguePresentation(id, { unread: true });
          if (data) boot.battleDialogue.force(data);
        },
      }),
    ],
  });
}
